import type {
  DirectAuthenticationDMPStatusContent,
  DmpConnectFilterCriteria,
  DmpConnectJSDmpDocumentContent,
  DmpConnectJSInsIdentityResult,
  FindDocumentsReponsePayload,
  GetPcsReaderResult,
  GetWebPsDmpUrlsPayload,
  InsiConnectorResult,
  ReadCpxCardResult,
  ReadVitaleCardResult,
  Result
} from "parog-web/cnam/types";
import type { NetworkParams } from "./dmp-connect";

export default class DMPConnectHttpInstance {
  declare sessionId: string;
  url = "https://localhost.icanopee.net:9982";

  private transactionsTimeoutInSeconds = 60;

  constructor(public accessToken: string) {}

  async _fetchDmpConnectJS(
    path: string,
    _body: Record<string, unknown>,
    callback: (result: any) => void,
    subPath = "api"
  ) {
    let token: string | null = null;
    if (this.sessionId) {
      token = await this.getOpenIDToken();
      await fetch(`${this.url}/${subPath}/hl_updateCpxAuthenticationToken`, {
        method: "POST",
        body: JSON.stringify({
          s_commandName: "hl_updateCpxAuthenticationToken",
          s_sessionId: this.sessionId,
          s_authenticationToken: token
        })
      });
    }

    return fetch(`${this.url}/${subPath}/${path}`, {
      method: "POST",
      body: JSON.stringify(
        Object.assign(
          {
            s_authenticationToken: token,
            s_commandName: path,
            s_sessionId: this.sessionId
          },
          _body
        )
      )
    })
      .then(async response => {
        const json = await response.json();
        if (json.s_status == "ERROR") {
          callback({ error: json });
          return;
        }
        callback(json);
      })
      .catch(error => {
        callback({ error: error });
      });
  }

  async getOpenIDToken(): Promise<string> {
    // This method is kept as it will probably be used when implementing DMP through Pro Sante Connect
    throw "Not implemented";
  }

  async hl_closeSession(callback: (result: any) => void) {
    if (!this.sessionId) {
      callback({ error: new Error("There is no current session") });
    }
    return this._fetchDmpConnectJS(
      "hl_closeSession",
      { s_sessionId: this.sessionId },
      callback
    );
  }

  async hl_createDmpConnector(
    dmpServerName: string,
    transactionsTimeoutInSeconds: number,
    practiceSetting: string,
    cpsPracticeLocationIndice: number,
    optionalParameters: Record<string, unknown>,
    callback: (result: Result) => void
  ) {
    let serverUrl: string;

    // Empty string => production DMP.
    if (dmpServerName === "")
      serverUrl = "https://lps2.dmp.gouv.fr/si-dmp-server/v2/services";
    else {
      if (dmpServerName.slice(0, 8) == "https://") {
        serverUrl = dmpServerName;
      } else {
        serverUrl =
          "https://" + dmpServerName + ".dmp.gouv.fr/si-dmp-server/v2/services";
      }
    }

    return this._fetchDmpConnectJS(
      "hl_createDmpConnector",
      Object.assign(
        {
          s_dmpUrl: serverUrl,
          i_transactionsTimeout: transactionsTimeoutInSeconds,
          s_practiceSetting: practiceSetting,
          i_cpsPracticeLocationIndice: cpsPracticeLocationIndice
        },
        optionalParameters
      ),
      callback
    );
  }

  async hl_createInsiConnector(
    tlsiServeurFullUrl: string,
    transactionsTimeoutInSeconds: number,
    practiceSetting: string,
    cpsPracticeLocationIndice: number,
    _optionalParameters: Record<string, unknown>,
    callback: (result: InsiConnectorResult) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_createTlsiConnector",
      {
        s_tlsiUrl: tlsiServeurFullUrl,
        i_transactionsTimeout: transactionsTimeoutInSeconds,
        s_practiceSetting: practiceSetting,
        i_cpsPracticeLocationIndice: cpsPracticeLocationIndice
      },
      callback
    );
  }

  async hl_findDocuments(
    nir: string,
    dmpConnectJSCriteria: DmpConnectFilterCriteria,
    callback: (result: FindDocumentsReponsePayload) => void
  ): Promise<void> {
    return this._fetchDmpConnectJS(
      "hl_findDocuments",
      Object.assign(
        {
          s_ins: nir
        },
        dmpConnectJSCriteria
      ) as unknown as Record<string, unknown>,
      callback
    );
  }

  async hl_getCpxCard(
    readerNumber: number,
    readerName: string,
    callback: (result: Result) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_getCpxCard",
      { i_readerNumber: readerNumber, s_readerName: readerName },
      callback
    );
  }

  async hl_getDirectAuthenticationDMPStatus(
    nir: string,
    callback: (result: DirectAuthenticationDMPStatusContent) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_getDirectAuthenticationDMPStatus",
      { s_ins: nir },
      callback
    );
  }

  async hl_getDocumentContentByUniqueId(
    nir: string,
    uniqueId: string,
    uuid: string,
    parameters: { i_getCdaHeaders: boolean; i_getCdaContent: boolean },
    callback: (result: DmpConnectJSDmpDocumentContent) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_getDocumentContentByUniqueId",
      Object.assign(
        { s_ins: nir, s_documentUniqueId: uniqueId, s_documentUuid: uuid },
        parameters
      ),
      callback
    );
  }

  async hl_getInsFromIdentityInformation(
    lpsInfoICanopee: Record<string, unknown>,
    birthName: string,
    given: string,
    sex: number,
    birthDate: string,
    birthPlace: string | undefined,
    callback: (result: DmpConnectJSInsIdentityResult) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_getInsFromIdentityInformation",
      {
        LpsInfos: lpsInfoICanopee,
        s_birthName: birthName,
        s_given: given,
        i_sex: sex,
        s_birthDate: birthDate,
        s_birthPlace: birthPlace
      },
      callback
    );
  }

  async hl_getPcscReaders(callback: (result: GetPcsReaderResult) => void) {
    return this._fetchDmpConnectJS("hl_getPcscReaders", {}, callback);
  }

  async hl_getVitaleCard(
    readerNumber: number,
    readerName: string,
    callback: (result: Result) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_getVitaleCard",
      { i_readerNumber: readerNumber, s_readerName: readerName },
      callback
    );
  }

  async hl_getWebPsDmpUrls(
    nir: string,
    i_getPscUrls: number,
    callback: (result: GetWebPsDmpUrlsPayload) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_getWebPsDmpUrls",
      { s_ins: nir, i_getPscUrls },
      callback
    );
  }

  async hl_openSession(
    cachedNetworkParams: NetworkParams | undefined,
    configuration: string,
    callback: (result: Record<string, unknown>) => void
  ) {
    const params = cachedNetworkParams ? cachedNetworkParams : {};
    return fetch(`${this.url}/api/hl_openSession`, {
      method: "POST",
      body: JSON.stringify(
        Object.assign(
          {
            s_commandName: "hl_openSession",
            s_sessionId: this.sessionId,
            i_timeoutInSeconds: this.transactionsTimeoutInSeconds,
            s_dcparameters64: configuration
          },
          params
        )
      )
    })
      .then(async response => {
        const data = await response.json();
        this.sessionId = data.s_sessionId;
        callback(data);
      })
      .catch(error => {
        callback({ error: error });
      });
  }

  async hl_openSessionWithCustomDcParam(
    _cachedDcParameters,
    _timeoutInSeconds,
    cachedNetworkParams,
    callback: (result: Record<string, unknown>) => void
  ) {
    return this.hl_openSession(
      cachedNetworkParams,
      cachedNetworkParams.configuration,
      callback
    );
  }

  async hl_readCpxCard(callback: (result: ReadCpxCardResult) => void) {
    return this._fetchDmpConnectJS("hl_readCpxCard", {}, callback);
  }

  async hl_readVitaleCard(callback: (result: ReadVitaleCardResult) => void) {
    return this._fetchDmpConnectJS("hl_readVitaleCard", {}, callback);
  }

  async hl_sessionExists(
    _options: object,
    callback: (result: Record<string, unknown>) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_sessionExists",
      { s_sessionId: this.sessionId },
      callback
    );
  }

  async hl_setConfidentialityLevel(
    enableSecretConnection: number,
    callback: (result: Result) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_setConfidentialityLevel",
      { i_enableSecretConnection: enableSecretConnection },
      callback
    );
  }

  async hl_updateUserDmpAccessAuthorization(
    nir: string,
    action: number,
    _isTreatingPhysician: number,
    _optionalParameters: Record<string, unknown> | null,
    callback: (result: Result) => void
  ) {
    return this._fetchDmpConnectJS(
      "hl_updateUserDmpAccessAuthorization",
      {
        s_ins: nir,
        i_action: action,
        i_setTreatingPhysician: _isTreatingPhysician
      },
      callback
    );
  }

  async sendHttpCommand(
    params: Record<string, unknown>,
    path: string,
    callback: (result: Record<string, unknown>) => void
  ) {
    return this._fetchDmpConnectJS(path, params, callback, "remotecommand");
  }
}
